import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SubscriptionPlan, TenantSubscription, AddOnPurchase, CostBreakdownItem, UsageSummary } from '../types';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Toast from '../components/ui/Toast';
import ShinyText from '../components/ui/ShinyText';
import { Plus, BookOpen, Workflow, Package, Calendar, CalendarClock, Sparkles } from 'lucide-react';
import { api } from '../services/api';
import { useSubscriptionError } from '../context/SubscriptionErrorContext';
import { handleApiError } from '../utils/errorHandling';
import { getPlanTier } from '../utils/planTierConfig';
import { useLocale } from '../context/LocaleContext';

interface GetPlansResponse {
    plans: SubscriptionPlan[];
}

const SubscriptionsPage: React.FC = () => {
    const navigate = useNavigate();
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [currentSub, setCurrentSub] = useState<TenantSubscription | null>(null);
    const [usage, setUsage] = useState<UsageSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submittingPlanId, setSubmittingPlanId] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ type: 'success' | 'info'; message: string } | null>(null);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const { setSubscriptionError } = useSubscriptionError();

    // Add-on state
    const [purchasedAddOns, setPurchasedAddOns] = useState<AddOnPurchase[]>([]);
    const [expandedAddOn, setExpandedAddOn] = useState<string | null>(null);
    const [purchasingAddOn, setPurchasingAddOn] = useState<string | null>(null);
    const [showAddOnConfirm, setShowAddOnConfirm] = useState<{
        type: string;
        label: string;
        price: number;
        priceSuffix?: string;
        description?: string;
        variant?: 'self_prepared' | 'mentoros_prepared';
    } | null>(null);
    const [showDeactivateConfirm, setShowDeactivateConfirm] = useState<{
        purchaseId: string;
        label: string;
    } | null>(null);

    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => {
                setNotification(null);
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [notification]);

    const handleCancel = async () => {
        setShowCancelConfirm(true);
    };

    const confirmCancel = async () => {
        setError(null);
        setSubmittingPlanId('cancel');

        try {
            await api.delete<TenantSubscription>('/api/subscriptions/tenant');
            setCurrentSub(null);
            setNotification({ type: 'info', message: 'Your subscription has been cancelled successfully.' });
            setSubscriptionError(null);
        } catch (err: any) {
            console.error('Cancellation error:', err);
            handleApiError(err, setError, setSubscriptionError);
        } finally {
            setSubmittingPlanId(null);
            setShowCancelConfirm(false);
        }
    };

    const fetchSubscriptionData = async () => {
        try {
            const subResponse = await api.get<TenantSubscription>('/api/subscriptions/tenant').catch(e => {
                if (e.code === 'TENANT0004') {
                    return null;
                }

                if (e.message && e.message.includes('404')) {
                    return null;
                }

                throw e;
            });

            if (subResponse && (!subResponse.planId || !subResponse.status)) {
                console.warn('Invalid subscription data structure:', subResponse);
                return null;
            }

            setCurrentSub(subResponse);
            return subResponse;
        } catch (err: any) {
            handleApiError(err, setError, setSubscriptionError);
            return null;
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                const usagePromise = api.get<UsageSummary>('/api/subscriptions/tenant/usage')
                    .then(data => setUsage(data))
                    .catch(() => setUsage(null));

                const plansPromise = api.get<GetPlansResponse>('/api/subscriptions/plans');

                const [plansResponse] = await Promise.all([
                    plansPromise,
                    fetchSubscriptionData(),
                    usagePromise
                ]);

                api.getAddOnPurchases()
                    .then(res => setPurchasedAddOns(res.purchases || []))
                    .catch(() => setPurchasedAddOns([]));

                const validPlans =
                    plansResponse.plans?.filter(
                        plan =>
                            plan &&
                            plan.planId &&
                            plan.name &&
                            plan.pricing &&
                            typeof plan.pricing.amount === 'number'
                    ) || [];

                setPlans(validPlans);
                setSubscriptionError(null);
            } catch (err: any) {
                handleApiError(err, setError, setSubscriptionError);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handlePurchaseAddOn = async (
        type: string,
        quantity?: number,
        variant?: 'self_prepared' | 'mentoros_prepared'
    ) => {
        setPurchasingAddOn(type);

        try {
            const res = await api.purchaseAddOn({ type: type as any, quantity, variant });

            if (res.success && res.purchase) {
                setPurchasedAddOns(prev => [...prev, res.purchase]);
                setNotification({
                    type: 'success',
                    message: `Add-on "${res.purchase.label}" purchased successfully!`
                });
                setExpandedAddOn(null);
            }
        } catch (err: any) {
            handleApiError(err, setError, setSubscriptionError);
        } finally {
            setPurchasingAddOn(null);
            setShowAddOnConfirm(null);
        }
    };

    const handleCancelAddOn = async (purchaseId: string, label: string) => {
        setPurchasingAddOn(purchaseId);
        setShowDeactivateConfirm(null);

        try {
            const res = await api.cancelAddonPurchase(purchaseId);

            if (res.purchase) {
                setPurchasedAddOns(prev =>
                    prev.map(addon =>
                        addon.id === purchaseId
                            ? {
                                    ...addon,
                                    status: res.purchase.status,
                                    cancelEffectiveAt: res.purchase.cancelEffectiveAt
                                }
                            : addon
                    )
                );
                setNotification({
                    type: 'info',
                    message: `Add-on "${label}" will be canceled at the end of this billing period.`
                });
            }
        } catch (err: any) {
            handleApiError(err, setError, setSubscriptionError);
        } finally {
            setPurchasingAddOn(null);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-full">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (error) {
        return <div className="text-center text-red-500">{error}</div>;
    }

    const tier = currentSub?.planId ? getPlanTier(currentSub.planId) : 'Unknown';
    const tierDisplay = tier.charAt(0).toUpperCase() + tier.slice(1);

    const { formatCurrency, formatDate } = useLocale();

    // Package type from /tenant/usage (BE: packageType is only on this endpoint)
    const packageType: 'fixed' | 'custom' = usage?.packageType || 'fixed';
    const isFixed = packageType === 'fixed';

    // AI usage cost: source of truth = usage.llmUsageCost (NOT monthlyCostToDate)
    const llmIncluded = usage?.llmUsageCost?.included || 0;
    const llmUsed = usage?.llmUsageCost?.used || 0;
    const llmExtra = usage?.llmUsageCost?.extra || 0;
    const llmShowExtra = usage?.llmUsageCost?.showExtraCost || false;

    const baseCost = currentSub?.billing?.amount || 0;
    // For fixed plans, end-of-cycle charge = extra only (0 when within included).
    // For custom plans, end-of-cycle charge = total usage cost.
    const endOfCycleUsageCost = isFixed ? llmExtra : llmUsed;
    const addOnsTotalCost = purchasedAddOns.reduce((sum, addon) => sum + addon.totalPrice, 0);

    // Plan-tier-aware Custom Actions monthly fee (Starter €19 / Growth €29 / Enterprise contact)
    const customActionsMonthly = tier === 'basic' ? 19 : tier === 'professional' ? 29 : 0;
    const customActionsMonthlyLabel = tier === 'enterprise'
        ? 'Contact sales'
        : `${formatCurrency(customActionsMonthly, 'EUR')}/mo`;

    // Cost breakdown rows — labels depend on packageType per UX spec.
    const baseRow: CostBreakdownItem = {
        key: 'base_cost',
        label: isFixed ? 'Package Cost' : 'Base Cost',
        sublabel: `${tierDisplay === 'Unknown' ? 'Standard' : tierDisplay} Plan · Billed at start of cycle`,
        amount: baseCost,
        currency: 'EUR'
    };
    const usageRow: CostBreakdownItem = {
        key: 'llm_usage',
        label: isFixed ? 'Extra AI Usage' : 'AI Usage',
        sublabel: isFixed
            ? (llmShowExtra
                ? `Billed at end of cycle · over ${formatCurrency(llmIncluded, 'EUR')} included`
                : `Within ${formatCurrency(llmIncluded, 'EUR')} included — no extra charge`)
            : 'Billed at end of cycle · live cumulative',
        amount: endOfCycleUsageCost,
        currency: 'EUR'
    };
    const breakdownItems: CostBreakdownItem[] = [baseRow, usageRow];
    const totalCost = baseCost + endOfCycleUsageCost + addOnsTotalCost;

    const isActiveRecurringStatus = (status?: AddOnPurchase['status']) =>
        status === 'active' || status === 'canceling';

    const isRecurringAddOnActive = (type: string) =>
        purchasedAddOns.some(
            addon =>
                addon.type === type &&
                addon.chargeModel === 'recurring' &&
                isActiveRecurringStatus(addon.status)
        );

    // Legacy recurring Domain Knowledge Pack purchases (kept for grandfathered tenants).
    const legacyDomainKnowledgePurchase = purchasedAddOns.find(
        addon =>
            addon.type === 'domain_knowledge' &&
            addon.chargeModel === 'recurring' &&
            isActiveRecurringStatus(addon.status)
    );

    // Recurring Custom Actions purchase (monthly fee, plan-tier-aware).
    const customActionsRecurring = purchasedAddOns.find(
        addon =>
            addon.type === 'custom_actions' &&
            addon.chargeModel === 'recurring' &&
            isActiveRecurringStatus(addon.status)
    );
    const isCustomActionsActive = isRecurringAddOnActive('custom_actions');
    const isOrderStatusPurchased = purchasedAddOns.some(a => a.type === 'order_status' && a.status !== 'canceled' && a.status !== 'expired');
    const isDomainKnowledgePurchased = purchasedAddOns.some(a => a.type === 'domain_knowledge' && a.status !== 'canceled' && a.status !== 'expired');

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 dark:text-text-primary text-light-text-primary ">My Subscription</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4 text-text-secondary dark:text-text-secondary text-light-text-secondary">Current Subscription Status</h2>
                
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <span className="text-text-secondary dark:text-text-secondary text-light-text-secondary">Status</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            currentSub?.status === 'active' ? 'bg-green-500/20 text-green-500' :
                            currentSub?.status === 'trial' ? 'bg-blue-500/20 text-blue-500' :
                            'bg-gray-500/20 text-gray-500'
                        }`}>
                            {(currentSub?.status || 'No Active Subscription').toUpperCase()}
                        </span>
                    </div>

                    {currentSub?.planId && (
                         <div className="flex justify-between items-center">
                            <span className="text-text-secondary dark:text-text-secondary text-light-text-secondary">Plan Type</span>
                            <span className="font-medium text-white dark:text-white text-gray-900">{tierDisplay}</span>
                         </div>
                    )}
                     
                     <div className="flex justify-between items-center">
                        <span className="text-text-secondary dark:text-text-secondary text-light-text-secondary">Next Billing</span>
                        <span className="text-white dark:text-white text-gray-900">
                             {currentSub?.billing?.nextBillingDate 
                                ? formatDate(currentSub.billing.nextBillingDate) 
                                : 'N/A'}
                        </span>
                     </div>

                     {currentSub?.status === 'active' && currentSub.planId && (
                        <div className="pt-4 mt-4 border-t border-border/50">
                             <Button 
                                variant="danger" 
                                className="w-full" 
                                onClick={handleCancel} 
                                isLoading={submittingPlanId === 'cancel'} 
                                disabled={submittingPlanId !== null}
                             >
                                Cancel Subscription
                             </Button>
                        </div>
                     )}
                </div>
            </Card>

            <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4 text-text-secondary dark:text-text-secondary text-light-text-secondary">Cost Breakdown</h2>
                
                <div className="space-y-4">
                    {breakdownItems.map((item) => (
                        <div key={item.key} className="flex justify-between items-center pb-4 border-b border-border/50">
                            <div>
                                <div className="text-text-secondary dark:text-text-secondary text-light-text-secondary">{item.label}</div>
                                <div className="text-xs text-text-tertiary">{item.sublabel}</div>
                            </div>
                            <span className="font-mono text-lg dark:text-white text-gray-900">{formatCurrency(item.amount, item.currency || 'EUR')}</span>
                        </div>
                    ))}

                    {purchasedAddOns.map((addon) => (
                        <div key={addon.id} className="flex justify-between items-center pb-4 border-b border-border/50">
                            <div>
                                <div className="text-text-secondary dark:text-text-secondary text-light-text-secondary">{addon.label}</div>
                                <div className="text-xs text-text-tertiary">
                                    {addon.type === 'extra_conversations' ? `${addon.quantity}x conversations` : 'Add-on'}
                                </div>
                            </div>
                            <span className="font-mono text-lg dark:text-white text-gray-900">{formatCurrency(addon.totalPrice, 'EUR')}</span>
                        </div>
                    ))}

                    <div className="flex justify-between items-center pt-2">
                        <span className="font-semibold text-lg text-white dark:text-white text-gray-900">Total</span>
                        <span className="font-mono text-2xl font-bold text-primary">{formatCurrency(totalCost, 'EUR')}</span>
                    </div>
                </div>
            </Card>
        </div>

        {/* Payment Schedule — clarifies the two billing moments */}
        <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4 text-text-secondary dark:text-text-secondary text-light-text-secondary">Payment Schedule</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Start-of-cycle charge */}
                <div className="rounded-lg border dark:border-border-color border-light-border-color p-4 dark:bg-surface bg-light-surface">
                    <div className="flex items-start gap-3">
                        <div className="text-primary mt-1"><Calendar className="w-5 h-5" /></div>
                        <div className="flex-1">
                            <div className="text-sm font-medium dark:text-white text-gray-900">
                                {isFixed ? 'Package Cost' : 'Base Cost'}
                            </div>
                            <div className="text-xs text-text-tertiary mt-0.5">
                                Charged at start of cycle
                                {currentSub?.billing?.currentPeriodStart
                                    ? ` · ${formatDate(currentSub.billing.currentPeriodStart)}`
                                    : ''}
                            </div>
                            <div className="font-mono text-2xl font-bold text-primary mt-2">
                                {formatCurrency(baseCost, 'EUR')}
                            </div>
                        </div>
                    </div>
                </div>

                {/* End-of-cycle charge */}
                <div className={`rounded-lg border p-4 dark:bg-surface bg-light-surface ${
                    isFixed && !llmShowExtra
                        ? 'dark:border-border-color border-light-border-color opacity-70'
                        : 'border-primary/40'
                }`}>
                    <div className="flex items-start gap-3">
                        <div className="text-primary mt-1"><CalendarClock className="w-5 h-5" /></div>
                        <div className="flex-1">
                            <div className="text-sm font-medium dark:text-white text-gray-900">
                                {isFixed ? 'Extra AI Usage' : 'AI Usage'}
                            </div>
                            <div className="text-xs text-text-tertiary mt-0.5">
                                Charged at end of cycle
                                {currentSub?.billing?.nextBillingDate
                                    ? ` · ${formatDate(currentSub.billing.nextBillingDate)}`
                                    : ''}
                                {isFixed && !llmShowExtra && (
                                    <span className="block mt-0.5">Within included allowance — no charge</span>
                                )}
                            </div>
                            <div className="font-mono text-2xl font-bold text-primary mt-2">
                                {formatCurrency(endOfCycleUsageCost, 'EUR')}
                            </div>
                            {isFixed && (
                                <div className="text-xs text-text-tertiary mt-1">
                                    Allowance: {formatCurrency(llmIncluded, 'EUR')} · Used: {formatCurrency(llmUsed, 'EUR')}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Card>

        <div className="text-center text-sm text-text-tertiary mt-8">
            * Costs are estimated and updated in real-time based on usage.
        </div>

        {/* Purchased Add-ons Section */}
        {purchasedAddOns.length > 0 && (
            <div className="mt-12 pt-8 border-t border-border/30">
                <h2 className="text-2xl font-bold text-center mb-8 dark:text-text-primary text-light-text-primary">Purchased Add-ons</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {purchasedAddOns.map((addon) => (
                        <div
                            key={addon.id}
                            className="dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color shadow-lg p-4"
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <span className="font-semibold dark:text-white text-gray-900">{addon.label}</span>
                                    {addon.quantity && addon.quantity > 1 && (
                                        <span className="text-sm text-text-tertiary ml-2">x{addon.quantity}</span>
                                    )}
                                </div>
                                <span className="font-mono text-sm font-semibold text-primary">
                                    {formatCurrency(addon.totalPrice, addon.currency || 'EUR')}
                                </span>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                                <div className="flex items-center gap-2">
                                    {addon.chargeModel === 'recurring' && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">Recurring</span>
                                    )}
                                    {addon.status === 'canceling' && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-500">
                                            Cancels {addon.cancelEffectiveAt ? formatDate(addon.cancelEffectiveAt) : 'at period end'}
                                        </span>
                                    )}
                                    {addon.status === 'active' && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-500">Active</span>
                                    )}
                                </div>
                                {addon.chargeModel === 'recurring' && addon.status === 'active' && (
                                    <Button
                                        size="sm"
                                        variant="secondary"
                                        className="text-xs"
                                        onClick={() => handleCancelAddOn(addon.id, addon.label)}
                                        isLoading={purchasingAddOn === addon.id}
                                        disabled={purchasingAddOn !== null}
                                    >
                                        Cancel
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )}

        {/* Available Add-ons Section */}
        <div className="mt-12 pt-8 border-t border-border/30">
            <h2 className="text-2xl font-bold text-center mb-2 dark:text-text-primary text-light-text-primary">Available Add-ons</h2>
            <p className="text-center text-sm text-text-tertiary mb-8">
                Extend Mentoros with optional capabilities. Prices match the public pricing page.
            </p>

            {/* Bundle — featured */}
            <div
                className={`relative mb-6 rounded-xl border-2 border-primary/60 dark:bg-surface bg-light-surface shadow-lg p-6 transition-colors ${
                    purchasingAddOn !== null ? '' : 'cursor-pointer hover:border-primary'
                }`}
                onClick={() => {
                    if (purchasingAddOn !== null) return;
                    setShowAddOnConfirm({
                        type: 'extension_bundle',
                        label: 'Extensions Bundle (all 3)',
                        price: 197,
                        description: 'One-time setup for Order Status Integration, Custom Actions setup, and Domain Knowledge Pack. Save €50 vs purchasing separately.'
                    });
                }}
            >
                <span className="absolute -top-3 right-4 bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Save €50
                </span>
                <div className="flex items-start gap-3">
                    <div className="mt-1 text-primary"><Package className="w-6 h-6" /></div>
                    <div className="flex-1">
                        <div className="flex items-center justify-between">
                            <ShinyText text="Bundle: all 3 extensions" className="font-semibold text-lg" />
                            <div className="flex items-center gap-2 ml-2">
                                <span className="font-mono text-sm text-text-tertiary line-through">{formatCurrency(247, 'EUR')}</span>
                                <span className="font-mono text-base font-semibold text-primary">{formatCurrency(197, 'EUR')}</span>
                            </div>
                        </div>
                        <p className="text-sm text-text-tertiary mt-1">
                            One-time setup: Order Status Integration + Custom Actions & Workflows + Domain Knowledge Pack (20% off setup fees).
                            Recurring Custom Actions fee still applies separately.
                        </p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Order Status Integration — one-time €49 */}
                <div
                    className={`dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color shadow-lg p-6 transition-colors ${
                        isOrderStatusPurchased
                            ? 'opacity-60'
                            : 'cursor-pointer hover:border-primary/50'
                    }`}
                    onClick={() => {
                        if (isOrderStatusPurchased || purchasingAddOn !== null) return;
                        setShowAddOnConfirm({
                            type: 'order_status',
                            label: 'Order Status Integration',
                            price: 49,
                            description: 'Connect Mentoros to your shipping/carrier or OMS API to deliver real-time order updates inside the conversation. One-time setup fee.'
                        });
                    }}
                >
                    <div className="flex items-start gap-3">
                        <div className="mt-1 text-primary"><Plus className="w-5 h-5" /></div>
                        <div className="flex-1">
                            <div className="flex items-center justify-between">
                                <ShinyText text="Order Status Integration" className="font-semibold text-lg" />
                                <div className="flex items-center gap-2">
                                    {isOrderStatusPurchased && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-500">Active</span>
                                    )}
                                    <span className="font-mono text-sm font-semibold text-primary ml-2">
                                        {formatCurrency(49, 'EUR')}
                                    </span>
                                </div>
                            </div>
                            <p className="text-sm text-text-tertiary mt-1">
                                Real-time order updates inside the conversation via your shipping/carrier or OMS API. <span className="text-text-secondary">One-time setup.</span>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Custom Actions & Workflows — setup €99 + monthly tiered */}
                <div className="dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color shadow-lg p-6">
                    <div className="flex items-start gap-3">
                        <div className="mt-1 text-primary"><Workflow className="w-5 h-5" /></div>
                        <div className="flex-1">
                            <div className="flex items-center justify-between">
                                <ShinyText text="Custom Actions & Workflows" className="font-semibold text-lg" />
                                {isCustomActionsActive && (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-500">Active</span>
                                )}
                            </div>
                            <p className="text-sm text-text-tertiary mt-1">
                                Specialized in-store actions (configurators, quoting, platform-specific flows). Includes up to 3 actions; +{formatCurrency(25, 'EUR')} per additional action.
                            </p>
                            <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                                <div className="rounded border dark:border-border-color border-light-border-color p-2">
                                    <div className="text-text-tertiary">Setup (one-time)</div>
                                    <div className="font-mono text-sm font-semibold text-primary mt-1">{formatCurrency(99, 'EUR')}</div>
                                </div>
                                <div className="rounded border dark:border-border-color border-light-border-color p-2">
                                    <div className="text-text-tertiary">Monthly ({tierDisplay === 'Unknown' ? 'plan-tier' : tierDisplay})</div>
                                    <div className="font-mono text-sm font-semibold text-primary mt-1">{customActionsMonthlyLabel}</div>
                                </div>
                            </div>
                            <div className="mt-4 flex items-center justify-between gap-3">
                                {isCustomActionsActive ? (
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        className="w-full"
                                        onClick={() => {
                                            if (customActionsRecurring && customActionsRecurring.status === 'active') {
                                                setShowDeactivateConfirm({
                                                    purchaseId: customActionsRecurring.id,
                                                    label: 'Custom Actions monthly subscription'
                                                });
                                            }
                                        }}
                                        disabled={purchasingAddOn !== null || customActionsRecurring?.status === 'canceling'}
                                    >
                                        {customActionsRecurring?.status === 'canceling' ? 'Cancels at period end' : 'Cancel monthly'}
                                    </Button>
                                ) : (
                                    <Button
                                        size="sm"
                                        className="w-full"
                                        onClick={() => {
                                            if (tier === 'enterprise') {
                                                navigate('/support');
                                                return;
                                            }
                                            setShowAddOnConfirm({
                                                type: 'custom_actions',
                                                label: `Custom Actions — setup + ${customActionsMonthlyLabel}`,
                                                price: 99 + customActionsMonthly,
                                                priceSuffix: ` (€99 setup + ${customActionsMonthlyLabel} recurring)`,
                                                description: 'You will be charged €99 once for setup and ' + customActionsMonthlyLabel + ' recurring (matches your current plan tier).'
                                            });
                                        }}
                                        disabled={purchasingAddOn !== null}
                                    >
                                        {tier === 'enterprise' ? 'Contact sales' : 'Purchase'}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Domain Knowledge Pack — one-time €99 */}
                <div
                    className={`dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color shadow-lg p-6 transition-colors ${
                        isDomainKnowledgePurchased
                            ? 'opacity-60'
                            : 'cursor-pointer hover:border-primary/50'
                    }`}
                    onClick={() => {
                        if (isDomainKnowledgePurchased || purchasingAddOn !== null) return;
                        setShowAddOnConfirm({
                            type: 'domain_knowledge',
                            label: 'Domain Knowledge Pack',
                            price: 99,
                            description: 'Add industry-specific knowledge (photography gear, skincare ingredients, automotive parts, etc.) using your approved resources. One-time setup fee.'
                        });
                    }}
                >
                    <div className="flex items-start gap-3">
                        <div className="mt-1 text-primary"><BookOpen className="w-5 h-5" /></div>
                        <div className="flex-1">
                            <div className="flex items-center justify-between">
                                <ShinyText text="Domain Knowledge Pack" className="font-semibold text-lg" />
                                <div className="flex items-center gap-2">
                                    {isDomainKnowledgePurchased && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-500">Active</span>
                                    )}
                                    <span className="font-mono text-sm font-semibold text-primary ml-2">
                                        {formatCurrency(99, 'EUR')}
                                    </span>
                                </div>
                            </div>
                            <p className="text-sm text-text-tertiary mt-1">
                                Train Mentoros on your niche (photography gear, skincare, automotive, etc.) using your approved resources. <span className="text-text-secondary">One-time setup.</span>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Legacy recurring DK card — grandfathered tenants only */}
                {legacyDomainKnowledgePurchase && (
                    <div className="dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color shadow-lg p-6">
                        <div className="flex items-start gap-3">
                            <div className="mt-1 text-text-tertiary"><Sparkles className="w-5 h-5" /></div>
                            <div className="flex-1">
                                <div className="flex items-center justify-between">
                                    <ShinyText text="Domain Knowledge Pack (legacy monthly)" className="font-semibold text-lg" />
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        legacyDomainKnowledgePurchase.status === 'canceling'
                                            ? 'bg-yellow-500/10 text-yellow-500'
                                            : 'bg-green-500/10 text-green-500'
                                    }`}>
                                        {legacyDomainKnowledgePurchase.status === 'canceling' ? 'Canceling' : 'Active'}
                                    </span>
                                </div>
                                <p className="text-sm text-text-tertiary mt-1">
                                    You are subscribed to the legacy monthly Domain Knowledge Pack ({formatCurrency(legacyDomainKnowledgePurchase.totalPrice, legacyDomainKnowledgePurchase.currency || 'EUR')}/mo). New purchases use the one-time pricing above.
                                </p>
                                {legacyDomainKnowledgePurchase.status === 'active' && (
                                    <Button
                                        size="sm"
                                        variant="secondary"
                                        className="mt-3"
                                        onClick={() => setShowDeactivateConfirm({
                                            purchaseId: legacyDomainKnowledgePurchase.id,
                                            label: 'Domain Knowledge Pack (legacy monthly)'
                                        })}
                                        disabled={purchasingAddOn !== null}
                                    >
                                        Cancel monthly
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <p className="text-center text-sm text-text-tertiary mt-6">
                One-time add-ons are non-refundable. Recurring fees can be canceled at any time and stop at the end of the current billing period.
            </p>
        </div>

        {/* Add-on confirmation modal */}
        {showAddOnConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80]">
                <Card title="Confirm Purchase" className="w-full max-w-md m-4">
                    <p className="dark:text-text-secondary text-light-text-secondary">
                        Purchase <span className="font-semibold dark:text-white text-gray-900">{showAddOnConfirm.label}</span> for{' '}
                        <span className="font-semibold text-primary">{formatCurrency(showAddOnConfirm.price, 'EUR')}</span>
                        {showAddOnConfirm.priceSuffix && (
                            <span className="text-text-tertiary">{showAddOnConfirm.priceSuffix}</span>
                        )}
                        ?
                    </p>
                    {showAddOnConfirm.description && (
                        <p className="text-sm dark:text-text-secondary text-light-text-secondary mt-2">
                            {showAddOnConfirm.description}
                        </p>
                    )}
                    <p className="text-sm dark:text-text-secondary text-light-text-secondary mt-2">
                        This will be added to your current billing period.
                    </p>
                    <div className="flex justify-end gap-4 mt-6">
                        <Button variant="secondary" onClick={() => setShowAddOnConfirm(null)} disabled={purchasingAddOn !== null}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => handlePurchaseAddOn(showAddOnConfirm.type, undefined, showAddOnConfirm.variant)}
                            isLoading={purchasingAddOn === showAddOnConfirm.type}
                        >
                            Confirm Purchase
                        </Button>
                    </div>
                </Card>
            </div>
        )}

        {/* Deactivation confirmation modal */}
        {showDeactivateConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80]">
                <Card title="Deactivate Add-on" className="w-full max-w-md m-4">
                    <p className="dark:text-text-secondary text-light-text-secondary">
                        Are you sure you want to deactivate <span className="font-semibold dark:text-white text-gray-900">{showDeactivateConfirm.label}</span>?
                    </p>
                    <p className="text-sm dark:text-text-secondary text-light-text-secondary mt-2">
                        The add-on will remain active until the end of your current billing period.
                    </p>
                    <div className="flex justify-end gap-4 mt-6">
                        <Button variant="secondary" onClick={() => setShowDeactivateConfirm(null)} disabled={purchasingAddOn !== null}>
                            Go Back
                        </Button>
                        <Button
                            onClick={() => handleCancelAddOn(showDeactivateConfirm.purchaseId, showDeactivateConfirm.label)}
                            isLoading={purchasingAddOn === showDeactivateConfirm.purchaseId}
                            disabled={purchasingAddOn !== null}
                        >
                            Confirm Deactivation
                        </Button>
                    </div>
                </Card>
            </div>
        )}

        {/* Mobile toast notification */}
        {notification && (
            <div className="fixed top-10 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)]">
            <Toast
                message={notification.message}
                type={notification.type}
                onClose={() => setNotification(null)}
                duration={5000}
            />
            </div>
        )}

        {showCancelConfirm && currentSub && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80]">
            <Card title="Cancel Subscription" className="w-full max-w-md m-4">
                <p className="dark:text-text-secondary text-light-text-secondary">
                Are you sure you want to cancel your subscription?
                </p>
                <p className="text-sm dark:text-text-secondary text-light-text-secondary mt-2">
                Your access will continue until the end of your current billing period on {currentSub.billing?.nextBillingDate ? formatDate(currentSub.billing.nextBillingDate) : 'N/A'}.
                </p>
                <div className="flex justify-end gap-4 mt-6">
                <Button variant="secondary" onClick={() => setShowCancelConfirm(false)} disabled={submittingPlanId === 'cancel'}>
                    Go Back
                </Button>
                <Button
                    variant="danger"
                    onClick={confirmCancel}
                    isLoading={submittingPlanId === 'cancel'}
                >
                    Confirm Cancellation
                </Button>
                </div>
            </Card>
            </div>
        )}
    </div>
  );
};

export default SubscriptionsPage;
